import {
    allVariants,
    collection,
    parentTypeFor,
    refs,
    routePath,
} from "../_variants/index.js";
import {
    cancelDocument,
    download,
    editDocument,
    revisionRollback,
    switchParent
} from "./actions";
import {
    thirdPartyDocs,
    thirdPartyDraws,
    thirdPartyOther
} from "../Miscellaneous/attachable.js";

import ItemListView from "../../../components/ItemListView/ItemListView.vue"
import ItemView from "../../../components/ItemView/ItemView.vue"
import { revisions as _revisions } from "../../standardAttachables";
import {
    appConfig
} from "../../../utils";
import { documentCheck } from "./internalAttachable";
import flightAttachables from "../FlightConditions/attachable?test";
import { makeConcessionAttachable } from "../Concessions/attachable.js";
import { makeDOIAttachable } from "../DOIs/attachable.js";
import { makeDQNAttachable } from "../DQNs/attachable.js";
import { makeDesignChangeAttachable } from "../DesignChanges/attachable.js";
import { makeDocumentsAttachable } from "./attachable.js";
import { makeServiceBulletinAttachable } from "../ServiceBulletins/attachable.js";
import partNumbers from "../ApprovedPartNumbers/attachable";
import trepRegs from "./trepRegs";

const revisions = {
    ..._revisions,
    "headers": [
        ..._revisions.headers
    ],
    "actions": [{
        "label": "Rollback Revision",
        "action": revisionRollback,
        show({ pageItem }) {
            return [Object.keys(pageItem.Revisions).length > 1, "No previous revisions found"];
        }
    }],
    "rowActions": [{
        "label": "View Archived File",
        async action({ toast, pageItem, rowItem, db }) {
            try {
                let docNumber = pageItem.Number.replace(".", "_");
                let rev = rowItem.Revision;
                let url = await db.download(`Database/Documents/${pageItem.ID}/${docNumber}_${rev}.pdf`);
                window.open(url, "__blank")
            } catch (err) {
                toast.error("Archive file not found");
            }
        }
    }]
};

const dataFields = [{
    "label": "Number",
    "key": "Number"
}, {
    "label": "Parent",
    "key": "Parent",
    "link": "Database/:ParentType/:ParentID"
}, {
    "label": "Mod Type",
    "key": "Type"
}, {
    "label": "Date Created",
    "key": "Date.Created",
    "type": "date"
}, {
    "label": "Date Completed",
    "key": "Date.Completed",
    "type": "date"
}, {
    "label": "Title",
    "key": "Title",
    "class": "md:col-span-3"
}];

const menuActions = [
    {
        "label": "Edit Document",
        "action": editDocument
    },
    {
        "label": "Cancel Document",
        "class": "text-red",
        "action": cancelDocument
    },
    {
        "label": "Change Main Parent",
        "action": switchParent
    },
    {
        "label": "Download Document",
        "break": true,
        "action": download
    }
];

/**
 * Build the list + detail vue-router routes for the Document register
 * matching the supplied variant.
 *
 * @param {import("../_variants/index.js").Variant} variant
 */
export const makeDocumentsRoutes = (variant) => {
    const isMilitary = variant.key === "military";
    const labelSingular = isMilitary ? "Military Document" : "Document";
    const labelPlural = isMilitary ? "Military Documents" : "Documents";
    const docParentType = parentTypeFor(variant, "Documents");
    const listPath = routePath(variant, "Documents");

    const routes = [{
        "path": listPath,
        "component": { ...ItemListView },
        "name": labelPlural,
        "meta": {
            "app": variant.app,
            "name": labelPlural,
            "sideNav": true,
            "sideNavSection": "Design Items",
            "appSwitcher": false,
            "icon": variant.appIcon,
            "tableSettings": docParentType,
            "dataPath": refs(variant, "Documents"),
            ...(appConfig.useElasticsearchLists && variant.collectionPrefix === "" ? { "esType": "Documents" } : {}),
            "selectionRoute": `Database/${docParentType}/{ID}`
        }
    }, {
        "path": `${listPath}/:id`,
        "component": ItemView,
        "name": labelSingular,
        "meta": {
            "app": variant.app,
            "name": labelPlural,
            "dataPath": collection(variant, "Documents"),
            "storeData": ["Database/DocumentTypes", "Database/Regulations/CS25"],
            title(item) {
                return `${labelPlural}: ${item.Number}`
            },
            "attachedItems": {
                "Revisions": {
                    "attachables": [revisions]
                },
                "Designs": {
                    "attachables": [makeDesignChangeAttachable(variant), flightAttachables]
                },
                "Design Items": {
                    "attachables": [makeDocumentsAttachable(variant), makeConcessionAttachable(variant), makeDQNAttachable(variant), makeDOIAttachable(variant), makeServiceBulletinAttachable(variant)]
                },
                "Part Numbers": {
                    "attachables": [partNumbers]
                },
                "Additional Information": {
                    "attachables": [
                        thirdPartyDocs,
                        thirdPartyDraws,
                        thirdPartyOther
                    ]
                },
                "TREP Regulations": {
                    "attachables": [trepRegs]
                },
                "AI Tools": {
                    "attachables": [documentCheck]
                }
            },
            "dataFields": dataFields,
            "menuActions": menuActions,
            "formMetadata": {
                "ParentType": docParentType
            }
        }
    }];

    // Technical Publications is a curated STC-only side-nav view; the Military
    // variant has no analogue today so we only emit it for STC to preserve
    // existing behaviour exactly.
    if (variant.key === "stc") {
        routes.push({
            "path": "/Database/TechnicalPublications",
            "component": { ...ItemListView },
            "name": "Technical Publications",
            "meta": {
                "app": "Database",
                "name": "Technical Publications",
                "sideNav": true,
                "sideNavSection": "Design Items",
                "tableSettings": "Documents",
                "dataPath": "Database/Refs/Documents",
                "selectionRoute": "Database/Documents/{ID}",
                filter(item) {
                    return ["TEC", "AMMS", "WDMS", "IPCS"].includes(item.Number.split(`${appConfig.docPrefix}`)[1]?.split("-")[0])
                }
            }
        });
    }

    return routes;
};

export default allVariants().flatMap(makeDocumentsRoutes);
